import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import styles from './css/SimpleYearComparisonChart.module.css';
import { LoadingWaves } from '../addons/LoadingWaves';

interface SimpleYearComparisonChartProps {
  data: {
    [year: string]: {
      [key: string]: number;
    };
  } | undefined;
}

export const SimpleYearComparisonChart: React.FC<SimpleYearComparisonChartProps> = ({ data }) => {
  const [horizontalGridPoints, setHorizontalGridPoints] = useState<number[]>([]);
  const [barSize, setBarSize] = useState(15);

  // Dynamic year colors - first year blue, second year light blue
  const availableYears = data ? Object.keys(data).sort() : [];
  const yearColors: { [year: string]: string } = {};
  if (availableYears.length > 0) {
    yearColors[availableYears[0]] = '#006DE5';  // Dark blue for first year
    if (availableYears.length > 1) {
      yearColors[availableYears[1]] = '#7DE7FF';  // Light blue for second year
    }
  }
  // Fallback for backward compatibility
  yearColors['2025'] = '#006DE5';
  yearColors['2040'] = '#7DE7FF';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1536px)');

    const updateGridPoints = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setHorizontalGridPoints(Array.from({ length: 18 }, (_, i) => -3 + i * 12));
      } else {
        setHorizontalGridPoints(Array.from({ length: 15 }, (_, i) => i * 12));
      }
    };

    updateGridPoints(mediaQuery);
    mediaQuery.addEventListener('change', updateGridPoints);

    return () => mediaQuery.removeEventListener('change', updateGridPoints);
  }, []);

  useEffect(() => {
    const numBars = Object.keys(data || {}).length;
    if (numBars === 1) setBarSize(100);
    else if (numBars >= 2 && numBars <= 4) setBarSize(100);
    else setBarSize(15);
  }, [data]);

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={yearColors['2025']} />
      </div>
    );
  }

  const years = Object.keys(data);
  const firstYear = years[0];
  const firstYearKeys = Object.keys(data[firstYear] || {});

  if (firstYearKeys.length === 0) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={yearColors['2025']} />
      </div>
    );
  }

  const innerKey = firstYearKeys[0];

  // Calculate Y ticks with EXACTLY 8 values including 0 and max
  const calculateFixedNumberOfTicks = (originalMax: number, numTicks: number = 8) => {
    const ticks: number[] = [];
    
    // Always include 0 as the first tick
    ticks.push(0);
    
    // Calculate step for exactly numTicks-1 remaining steps to reach max
    const step = originalMax / (numTicks - 1);
    
    // Generate all ticks except 0 (already added) and max (will be added separately)
    for (let i = 1; i < numTicks - 1; i++) {
      const value = i * step;
      ticks.push(value);
    }
    
    // Always include the max value as the last tick
    ticks.push(originalMax);
    
    return ticks;
  };

  // Extract original values
  const originalValues = years.map((year) => data[year][innerKey] || 0);
  
  // Find the maximum value for scaling
  const maxValue = Math.max(...originalValues, 1); // Use at least 1 to avoid division by zero
  
  // Generate exactly 8 ticks
  const fixedTickValues = calculateFixedNumberOfTicks(maxValue);
  
  // Scale these to the display range
  const displayMaxValue = 10000;
  const yDisplayTicks = fixedTickValues.map(val => 
    (val / maxValue) * displayMaxValue
  );

  // Apply scaling to values
  const scaledValues: Record<string, number> = {};
originalValues.forEach((value, index) => {
  const year = years[index];
  
  // Scale to display range
  let scaledValue = (value / maxValue) * displayMaxValue;
  
  // Ensure small non-zero values have a minimum visible height (e.g., 5% of chart height)
  const minVisibleHeight = displayMaxValue * 0.03; // 3% of chart height
  
  if (value > 0 && scaledValue < minVisibleHeight) {
    scaledValue = minVisibleHeight;
  }
  
  scaledValues[year] = scaledValue;
});

  // Create final display data
  const displayData = years.map((year) => {
    const originalValue = data[year][innerKey] || 0;
    
    return {
      year,
      [innerKey]: scaledValues[year],
      [`${innerKey}_original`]: originalValue,
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const originalValue = payload[0]?.payload?.[`${innerKey}_original`] || 0;

    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <p className="text-sm font-medium mb-1">{label}</p>
        <div className="flex items-center gap-2 text-sm">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: yearColors[label] || '#A3A3A3' }}
          />
          <span>{innerKey}:</span>
          <span className="font-medium">{originalValue.toLocaleString()}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%">
        <BarChart
          key={JSON.stringify(displayData)}
          data={displayData}
          margin={{ top: 20, right: 0, left: -15, bottom: 20 }}
          barGap={8}
          barCategoryGap={40}
        >
          <CartesianGrid
            horizontal={true}
            vertical={false}
            strokeDasharray="0"
            stroke="#DADEE5"
            strokeWidth={1}
            horizontalPoints={horizontalGridPoints.length > 0 ? horizontalGridPoints : undefined}
          />

          <XAxis
            dataKey="year"
            axisLine={false}
            tickLine={false}
            tick={{
              fill: '#6C7693',
              fontSize: 10
            }}
            tickMargin={8}
          />
          <YAxis
            hide={false}
            ticks={yDisplayTicks}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6C7693', fontSize: 10 }}
            width={40}
            domain={[0, displayMaxValue]}
            tickFormatter={(val: number) => {
              // Handle the 0 case
              if (val === 0 || Math.abs(val) < 0.001) return "0";
              
              // Get the index of this tick value
              const index = yDisplayTicks.findIndex(t => Math.abs(t - val) < 0.001);
              if (index === -1) return ""; // Safety check
              
              // Get the original value this represents
              const originalValue = fixedTickValues[index];
              
              // Format the value with appropriate units
              if (originalValue >= 1_000_000_000) {
                // For billions, add more precision to ensure uniqueness
                const billions = originalValue / 1_000_000_000;
                if (billions < 1) {
                  return `${Math.round(originalValue / 1_000_000)}M`;
                }
                // Use offset rounding for unique labels
                return `${(Math.floor(billions * 100 + index) / 100).toFixed(1).replace(/\.0$/, '')}B`;
              }
              
              if (originalValue >= 1_000_000) {
                // For millions
                const millions = originalValue / 1_000_000;
                
                // Special handling for values that round to the same million
                // For values between 1M and 2M, show more precision as needed
                if (millions < 1) {
                  return `${Math.round(originalValue / 1_000)}K`;
                } else if (millions >= 1 && millions < 2) {
                  // Use 1 decimal for these values to avoid duplicates
                  if (index === 4) return "1M";
                  if (index === 5) return "1.3M";
                  if (index === 6) return "1.7M";
                  return `${(Math.floor(millions * 10 + index) / 10).toFixed(1).replace(/\.0$/, '')}M`;
                } else {
                  // Use offset rounding for unique labels
                  return `${(Math.floor(millions * 10 + index) / 10).toFixed(1).replace(/\.0$/, '')}M`;
                }
              }
              
              if (originalValue >= 1_000) {
                // For thousands
                const thousands = originalValue / 1_000;
                // Use a small index offset to ensure uniqueness
                return `${(Math.floor(thousands * 10 + index) / 10).toFixed(1).replace(/\.0$/, '')}K`;
              }
              
              // For smaller values
              return `${Math.floor(originalValue + index * 0.4)}`;
            }}
          />
          <Tooltip
            content={CustomTooltip}
            cursor={{ fill: 'transparent' }}
          />
          <Bar
            dataKey={innerKey}
            radius={[4, 4, 0, 0]}
            barSize={barSize}
            className={styles.bar}
            isAnimationActive={false}
          >
            {displayData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={yearColors[entry.year] || '#A3A3A3'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};